import React, { useState, useCallback, useContext, useEffect, useRef, createContext } from 'react';
import { NodeViewWrapper, NodeViewContent } from '@tiptap/react';
import { motion, AnimatePresence } from 'framer-motion';
import { GripVertical, Archive, Sparkles, Loader2, Check } from 'lucide-react';

// Context for paragraph actions - allows parent components to provide handlers
export const ParagraphActionsContext = createContext({
    onAddToVault: null,
    onAddToMariko: null,
    onOpenVault: null,
    onOpenMariko: null,
});

/**
 * ParagraphNodeView Component
 * 
 * A custom Tiptap node view that wraps paragraphs with Notion-style hover controls.
 * Features:
 * - Drag handle icon positioned at the start of the paragraph (for dragging only)
 * - Floating pill with action buttons appears when text is selected within the paragraph
 * - Visual highlight when dragging
 */
const ParagraphNodeView = ({ node, editor, getPos }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [isDragHandleActive, setIsDragHandleActive] = useState(false);
    const [selectedText, setSelectedText] = useState('');
    const [showActionPill, setShowActionPill] = useState(false);
    const [vaultStatus, setVaultStatus] = useState('idle'); // 'idle', 'saving', 'success', 'error'
    const paragraphRef = useRef(null);

    // Get action handlers from context
    const { onAddToVault, onAddToMariko, onOpenVault, onOpenMariko } = useContext(ParagraphActionsContext);

    // Get paragraph text from node content
    const paragraphText = node.textContent || '';

    /**
     * Highlight the entire paragraph when drag handle is engaged
     */
    const highlightParagraph = useCallback(() => {
        if (!paragraphRef.current) return;

        const paragraphElement = paragraphRef.current.querySelector('.paragraph-content') || paragraphRef.current;
        if (paragraphElement) {
            // Create a range and select the entire paragraph
            const range = document.createRange();
            range.selectNodeContents(paragraphElement);
            const selection = window.getSelection();
            selection.removeAllRanges();
            selection.addRange(range);
        }
    }, []);

    /**
     * Clear paragraph highlight
     */
    const clearHighlight = useCallback(() => {
        const selection = window.getSelection();
        if (selection) {
            selection.removeAllRanges();
        }
    }, []);

    /**
     * Handle drag start for the paragraph
     */
    const handleDragStart = useCallback((e) => {
        if (!paragraphText) return;

        setIsDragging(true);
        setIsDragHandleActive(true);
        highlightParagraph();

        const artifact = {
            id: `artifact-${Date.now()}`,
            title: paragraphText.slice(0, 50) + (paragraphText.length > 50 ? '...' : ''),
            content: paragraphText,
            createdAt: new Date().toISOString(),
            category: 'snippet'
        };

        e.dataTransfer.setData('application/json', JSON.stringify(artifact));
        e.dataTransfer.setData('text/plain', paragraphText);
        e.dataTransfer.effectAllowed = 'copy';
    }, [paragraphText, highlightParagraph]);

    /**
     * Handle drag end
     */
    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
        setIsDragHandleActive(false);
        // Keep highlight briefly then clear
        setTimeout(() => {
            clearHighlight();
        }, 100);
    }, [clearHighlight]);

    /**
     * Handle mouse down on drag handle - start highlighting
     */
    const handleDragHandleMouseDown = useCallback(() => {
        setIsDragHandleActive(true);
        highlightParagraph();
    }, [highlightParagraph]);

    /**
     * Handle mouse up on drag handle - end highlighting if not dragging
     */
    const handleDragHandleMouseUp = useCallback(() => {
        if (!isDragging) {
            setIsDragHandleActive(false);
            clearHighlight();
        }
    }, [isDragging, clearHighlight]);

    /**
     * Handle "Add to Vault" click - saves artifact and opens vault drawer
     */
    const handleAddToVault = useCallback(async () => {
        if (!selectedText) return;

        setVaultStatus('saving');

        try {
            if (onAddToVault) {
                await onAddToVault(selectedText);
            }
            setVaultStatus('success');

            // Open the vault drawer after a brief delay
            setTimeout(() => {
                if (onOpenVault) {
                    onOpenVault();
                }
            }, 500);

            // Clear selection after a brief delay to show success
            setTimeout(() => {
                setSelectedText('');
                setShowActionPill(false);
                setVaultStatus('idle');
                // Clear browser selection
                window.getSelection()?.removeAllRanges();
            }, 1000);
        } catch (error) {
            console.error('Failed to add to vault:', error);
            setVaultStatus('error');
            // Reset after error
            setTimeout(() => {
                setVaultStatus('idle');
            }, 2000);
        }
    }, [onAddToVault, onOpenVault, selectedText]);

    /**
     * Handle "Add to Mariko" click - sends to AI copilot and opens AI assistant
     */
    const handleAddToMariko = useCallback(() => {
        if (onAddToMariko && selectedText) {
            onAddToMariko(selectedText);
        }
        // Open the AI assistant modal
        if (onOpenMariko) {
            onOpenMariko();
        }
        // Clear selection
        setSelectedText('');
        setShowActionPill(false);
        // Clear browser selection
        window.getSelection()?.removeAllRanges();
    }, [onAddToMariko, onOpenMariko, selectedText]);

    /**
     * Listen for text selection within this paragraph
     */
    useEffect(() => {
        const handleSelectionChange = () => {
            // Skip if we're in drag mode
            if (isDragHandleActive) return;

            const selection = window.getSelection();

            if (!selection || selection.isCollapsed) {
                setSelectedText('');
                setShowActionPill(false);
                return;
            }

            const selectedTextContent = selection.toString().trim();

            if (!selectedTextContent) {
                setSelectedText('');
                setShowActionPill(false);
                return;
            }

            // Check if selection is within this paragraph
            if (paragraphRef.current) {
                const range = selection.getRangeAt(0);
                const paragraphElement = paragraphRef.current.querySelector('.paragraph-content') || paragraphRef.current;

                if (paragraphElement && paragraphElement.contains(range.commonAncestorContainer)) {
                    setSelectedText(selectedTextContent);
                    setShowActionPill(true);
                } else {
                    setSelectedText('');
                    setShowActionPill(false);
                }
            }
        };

        document.addEventListener('selectionchange', handleSelectionChange);

        return () => {
            document.removeEventListener('selectionchange', handleSelectionChange);
        };
    }, [isDragHandleActive]);

    // Check if paragraph has meaningful content
    const hasContent = paragraphText.trim().length > 0;

    return (
        <NodeViewWrapper
            ref={paragraphRef}
            className={`relative group transition-colors duration-150 ${isDragHandleActive ? 'bg-indigo-50 rounded-md' : ''
                }`}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            {/* Drag Handle - Only shows on hover, for dragging only */}
            {hasContent && isHovered && (
                <div
                    className="absolute left-0 top-0 z-20 flex items-start -translate-x-full pr-2 pointer-events-auto"
                    contentEditable={false}
                    draggable={false}
                >
                    <div
                        draggable
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                        onMouseDown={handleDragHandleMouseDown}
                        onMouseUp={handleDragHandleMouseUp}
                        className={`
                            flex items-center justify-center w-8 h-8 rounded-md cursor-grab active:cursor-grabbing
                            transition-all duration-150
                            ${isDragging || isDragHandleActive
                                ? 'bg-indigo-100 text-indigo-600 scale-110'
                                : 'text-slate-300 hover:text-slate-500 hover:bg-slate-100'
                            }
                        `}
                        title="Drag to vault"
                    >
                        <GripVertical size={18} />
                    </div>
                </div>
            )}

            {/* Floating Action Pill - Appears at top of paragraph when text is selected */}
            <AnimatePresence>
                {selectedText && showActionPill && (
                    <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.95 }}
                        transition={{
                            type: 'spring',
                            stiffness: 400,
                            damping: 25,
                            duration: 0.2
                        }}
                        className="absolute top-0 left-4 -translate-y-full z-50 pointer-events-auto"
                        style={{ marginTop: '-40px' }}
                        contentEditable={false}
                    >
                        <div className="flex items-center gap-1 px-3 py-2 bg-white rounded-full shadow-lg border border-slate-200/80 backdrop-blur-sm">
                            {/* Add to Vault Button */}
                            <button
                                onClick={handleAddToVault}
                                disabled={vaultStatus === 'saving'}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${vaultStatus === 'saving'
                                    ? 'text-indigo-500 cursor-wait'
                                    : vaultStatus === 'success'
                                        ? 'text-green-600 bg-green-50'
                                        : vaultStatus === 'error'
                                            ? 'text-red-600 bg-red-50'
                                            : 'text-slate-600 hover:text-indigo-600 hover:bg-indigo-50'
                                    }`}
                                title="Save to Vault"
                            >
                                {vaultStatus === 'saving' ? (
                                    <>
                                        <Loader2 size={14} className="animate-spin" />
                                        <span>Saving...</span>
                                    </>
                                ) : vaultStatus === 'success' ? (
                                    <>
                                        <Check size={14} />
                                        <span>Saved!</span>
                                    </>
                                ) : vaultStatus === 'error' ? (
                                    <>
                                        <Archive size={14} />
                                        <span>Failed</span>
                                    </>
                                ) : (
                                    <>
                                        <Archive size={14} />
                                        <span>Add to Vault</span>
                                    </>
                                )}
                            </button>

                            {/* Divider */}
                            <div className="w-px h-5 bg-slate-200" />

                            {/* Add to Mariko Button */}
                            <button
                                onClick={handleAddToMariko}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-colors"
                                title="Send to AI Assistant"
                            >
                                <Sparkles size={14} />
                                <span>Add to Mariko</span>
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Paragraph Content */}
            <NodeViewContent className="paragraph-content" />
        </NodeViewWrapper>
    );
};

export default ParagraphNodeView;
